// Page templates (ui-spec §3): every console page is a ListPage or a DetailPage (or a create flow). Product pages
// pass their data and columns; the templates own layout, the header rules, sub-navigation, View YAML and states.
import { ApiError, errorView, linkTo, objectPath, useApi, type KObject } from "@nodus/api";
import {
  AppLink,
  Button,
  CodeSnippet,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  cliCommand,
  DetailSkeleton,
  EmptyState,
  ErrorState,
  FilterBarLead,
  LinkSegments,
  LinkTabs,
  ListFooter,
  ListSkeleton,
  Page,
  PageHeader,
  ResourceHeader,
  StateAction,
  toast,
  usePrefs,
  YamlSheet,
  type Crumb,
  type LinkTab,
  type MenuAction,
  type ResourceHeaderProps,
} from "@nodus/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import * as React from "react";
import { stringify } from "yaml";
import { DetailTabStrip, detailTabs, movedTab, useDetailNav } from "./detail-nav";
import { SectionHeader } from "./section";

const LIST_KEY = "nodus.list:";

/** The breadcrumb back to a list returns to it with the filters the user left (ui-spec §2.5), for this tab only. */
function listHrefWithFilters(href: string | undefined): string | undefined {
  if (!href) return href;
  try {
    return sessionStorage.getItem(LIST_KEY + href.split("?")[0]) ?? href;
  } catch {
    return href;
  }
}

/** "0 runs" under an empty state repeats it, so the footer is left out. */
function isZeroCount(count: React.ReactNode): boolean {
  return typeof count === "string" && /^0\b/.test(count);
}

/**
 * A list page (ui-spec §3.1, dashboard layout): the section's title row (its views as tabs), then the page's own
 * views as segments at the start of the filter bar, the table, and a one-line count.
 */
export function ListPage({
  title,
  description,
  cli,
  actions,
  secondaryActions,
  tabs,
  tabsVariant = "filters",
  activeTab,
  filters,
  count,
  loading,
  error,
  onRetry,
  views,
  children,
}: {
  title: string;
  description?: string;
  cli: string;
  actions?: React.ReactNode;
  secondaryActions?: MenuAction[];
  /** Keep the section's title row on a path that is not a section view (Invites under Settings › Members). */
  views?: boolean;
  /** The page's own views (Active, History, All), drawn as segments. */
  tabs?: LinkTab[];
  /** Product sections use underline navigation; status filters keep compact segments. */
  tabsVariant?: "filters" | "navigation";
  activeTab?: string;
  /** A FilterBar. */
  filters?: React.ReactNode;
  /** Footer count: "2 jobs" (or a floor, "500+ jobs"). */
  count?: React.ReactNode;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  children: React.ReactNode;
}) {
  React.useEffect(() => {
    document.title = `${title} · Nodus`;
  }, [title]);
  React.useEffect(() => {
    try {
      sessionStorage.setItem(LIST_KEY + window.location.pathname, window.location.pathname + window.location.search);
    } catch {
      // Without storage the breadcrumb returns to the unfiltered list.
    }
  });
  const segments = tabs && tabsVariant === "filters" ? <LinkSegments label={`${title} views`} tabs={tabs} active={activeTab ?? tabs[0]?.id ?? ""} /> : null;
  return (
    <Page className="mx-auto max-w-[1440px]">
      <SectionHeader title={title} description={description} cli={cli} actions={actions} secondaryActions={secondaryActions} {...(views ? { views } : {})} />
      {tabs && tabsVariant === "navigation" ? <div className="-mt-3 mb-5"><LinkTabs label={`${title} views`} tabs={tabs} active={activeTab ?? tabs[0]?.id ?? ""} /></div> : null}
      {loading ? (
        <ListSkeleton />
      ) : (
        <>
          {filters ? <FilterBarLead.Provider value={segments}>{filters}</FilterBarLead.Provider> : segments ? <div className="mb-3">{segments}</div> : null}
          {error ? <ErrorState error={errorView(error)} {...(onRetry ? { onRetry } : {})} /> : children}
          {count !== undefined && !error && !isZeroCount(count) ? <ListFooter count={count} /> : null}
        </>
      )}
    </Page>
  );
}

export interface DetailPageProps {
  kind: string;
  name: string;
  scope: { org: string; project: string };
  tab: string;
  /** Product URL for a shared API kind, including the object name. */
  basePath?: string;
  /** Parents first (`Runs`, then a Pipeline), drawn as `Runs / name`. */
  crumbs: Crumb[];
  object?: KObject;
  loading?: boolean;
  error?: unknown;
  notFound?: boolean;
  onRetry?: () => void;
  /** What a customer calls the object (`run` for a TrainingJob); defaults to the kind. */
  noun?: string;
  /** Tabs the spec does not have (Members on a single-node Job). */
  withoutTabs?: string[];
  header: Omit<ResourceHeaderProps, "name" | "actions" | "cli"> & {
    cli?: string;
    actions?: MenuAction[];
    /** The one state action the header shows as a button (Cancel, Resume, Start); the rest stay in `⋯`. */
    primary?: MenuAction;
  };
  /** The 288px right aside (collapses under the body below xl). */
  aside?: React.ReactNode;
  /** Offers Edit and apply in the YAML sheet (mutable fields, compare-and-swap on resourceVersion). */
  editable?: boolean;
  /** The object's common commands for the `?sheet=cli` sheet, each with its Python equivalent. */
  commands?: { label: string; cli: string; python?: string }[];
  children: React.ReactNode;
}

/** The shared header actions of every detail page, before the kind's state actions (ui-spec §3.3 `⋯` menu). */
function standardActions(p: DetailPageProps, openYaml: () => void): MenuAction[] {
  const copy = (label: string, text: string) => ({
    label,
    onSelect: () => {
      void navigator.clipboard?.writeText(text).then(() => toast(`${label.replace("Copy ", "")} copied`)).catch(() => undefined);
    },
  });
  const link = `${window.location.origin}${p.basePath ? `${p.basePath}/overview` : linkTo({ kind: p.kind, name: p.name }, p.scope)}`;
  const actions: MenuAction[] = [
    { label: "View YAML", onSelect: openYaml },
    copy("Copy CLI command", p.header.cli ?? `nodus describe ${p.kind.toLowerCase()}/${p.name}`),
    copy("Copy name", p.name),
    ...(p.object?.metadata.uid ? [copy("Copy uid", p.object.metadata.uid)] : []),
    copy("Copy link", link),
  ];
  if (p.object) {
    actions.push({
      label: "Download manifest",
      onSelect: () => {
        const { status: _s, ...rest } = p.object as KObject & { status?: unknown };
        const blob = new Blob([stringify(rest)], { type: "application/yaml" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${p.name}.yaml`;
        a.click();
        URL.revokeObjectURL(a.href);
      },
    });
  }
  return actions;
}

export function DetailPage(p: DetailPageProps) {
  const { client } = useApi();
  const prefs = usePrefs();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { sheet?: string };
  const yamlOpen = search.sheet === "yaml";
  const cliOpen = search.sheet === "cli";
  const setSheet = React.useCallback(
    (sheet: "yaml" | "cli" | undefined) => void navigate({ to: ".", search: (s: Record<string, unknown>) => ({ ...s, sheet }), replace: true }),
    [navigate],
  );
  const setYaml = React.useCallback((open: boolean) => setSheet(open ? "yaml" : undefined), [setSheet]);
  useDetailNav(detailTabs(p.kind, p.name, p.scope, p.tab, p.withoutTabs, p.basePath));

  React.useEffect(() => {
    document.title = `${p.name} · ${p.crumbs[0]?.label ?? p.kind} · Nodus`;
  }, [p.name, p.crumbs, p.kind]);


  // A former tab's URL lands on the tab that now holds its section, scrolled to it, with its query kept.
  const moved = movedTab(p.kind, p.tab);
  React.useEffect(() => {
    if (!moved) return;
    const href = p.basePath ? `${p.basePath}/${moved}` : linkTo({ kind: p.kind, name: p.name }, { ...p.scope, tab: moved });
    window.history.replaceState(window.history.state, "", `${href}${window.location.search}#${p.tab}`);
    dispatchEvent(new PopStateEvent("popstate", { state: window.history.state }));
  }, [moved, p.kind, p.name, p.scope, p.tab, p.basePath]);

  const parents = p.crumbs.map((c, i) => (i === 0 && c.href ? { ...c, href: listHrefWithFilters(c.href)! } : c));
  if (p.loading || moved) {
    return (
      <Page className="mx-auto max-w-[1440px]">
        <DetailSkeleton />
      </Page>
    );
  }
  if (p.notFound) {
    return (
      <Page className="mx-auto max-w-[1440px]">
        <PageHeader title={p.name} parents={parents} />
        <EmptyState
          title={`${p.noun ? p.noun[0]!.toUpperCase() + p.noun.slice(1) : p.kind} not found`}
          description={`No ${p.noun ?? p.kind.toLowerCase()} named ${p.name} in project ${p.scope.project}. It may have been deleted, or it is in another project.`}
          cli={cliCommand(["get", `${p.kind.toLowerCase()}s`, "-A"], { org: p.scope.org }, { omitScope: prefs.omitScope })}
          actions={
            parents[0]?.href ? (
              <Button asChild variant="outline">
                <AppLink href={parents[0].href}>Back to {parents[0].label}</AppLink>
              </Button>
            ) : null
          }
        />
      </Page>
    );
  }
  if (p.error) {
    return (
      <Page className="mx-auto max-w-[1440px]">
        <PageHeader title={p.name} parents={parents} />
        <ErrorState error={errorView(p.error)} onRetry={p.onRetry} />
      </Page>
    );
  }
  const cli = p.header.cli ?? cliCommand(["describe", `${p.kind.toLowerCase()}/${p.name}`], p.scope, { omitScope: prefs.omitScope });
  const { primary, ...header } = p.header;
  return (
    <Page className="mx-auto max-w-[1440px]">
      {parents.length ? (
        <nav aria-label="Breadcrumb" className="mb-3">
          <ol className="flex flex-wrap items-center gap-2 text-small text-fg-muted">
            {parents.map((parent, index) => (
              <li key={`${parent.label}-${index}`} className="flex min-w-0 items-center gap-2">
                {index > 0 ? <ChevronRight className="size-3 shrink-0" aria-hidden /> : null}
                {parent.href ? <AppLink href={parent.href} className="hover:text-fg-primary">{parent.label}</AppLink> : <span>{parent.label}</span>}
              </li>
            ))}
          </ol>
        </nav>
      ) : null}
      <ResourceHeader
        {...header}
        {...(primary ? { primaryAction: <StateAction disabled={primary.disabled} onClick={primary.onSelect}>{primary.label}</StateAction> } : {})}
        name={p.name}
        cli={cli}
        actions={[
          ...standardActions({ ...p, header: { ...p.header, cli } }, () => setYaml(true)),
          ...(p.commands?.length ? [{ label: "All CLI commands", onSelect: () => setSheet("cli") }] : []),
          ...(p.header.actions ?? []).filter((a) => a.label !== primary?.label),
        ]}
      />
      <DetailTabStrip />
      <div className={p.aside ? "mt-5 grid gap-8 xl:grid-cols-[minmax(0,1fr)_var(--nd-detail-aside-width)]" : "mt-5"}>
        <div className="nd-detail-content min-w-0">{p.children}</div>
        {p.aside ? <aside className="nd-detail-aside min-w-0 self-start rounded-lg border border-line bg-subtle p-5">{p.aside}</aside> : null}
      </div>
      {p.commands?.length ? (
        <Sheet open={cliOpen} onOpenChange={(o) => setSheet(o ? "cli" : undefined)}>
          <SheetContent width={720}>
            <div className="border-b border-line-subtle px-5 py-4 pr-12">
              <SheetTitle>CLI commands</SheetTitle>
              <SheetDescription className="font-mono text-small">nodus config use-context {p.scope.org}</SheetDescription>
            </div>
            <div className="grid gap-5 overflow-auto p-5">
              {p.commands.map((c) => (
                <div key={c.label} className="grid gap-2">
                  <h3 className="text-ui font-medium text-fg-primary">{c.label}</h3>
                  <CodeSnippet variants={[{ id: "cli", label: "CLI", code: c.cli }, ...(c.python ? [{ id: "python", label: "Python", code: c.python }] : [])]} />
                </div>
              ))}
            </div>
          </SheetContent>
        </Sheet>
      ) : null}
      {p.object ? (
        <YamlSheet
          open={yamlOpen}
          onOpenChange={setYaml}
          object={p.object as unknown as Record<string, unknown>}
          fileName={`${p.name}.yaml`}
          {...(p.editable
            ? {
                onApply: async (patch: unknown, resourceVersion: string | undefined) => {
                  const path = objectPath(p.kind, p.name, p.scope.project);
                  const body = { ...(patch as Record<string, unknown>) } as { metadata?: Record<string, unknown> };
                  body.metadata = { ...(body.metadata ?? {}), ...(resourceVersion ? { resourceVersion } : {}) };
                  try {
                    await client.patch(path, body, { org: p.scope.org });
                    return { ok: true as const };
                  } catch (err) {
                    if (err instanceof ApiError && err.code === 409) {
                      const latest = await client.get<Record<string, unknown>>(path, { org: p.scope.org });
                      return { conflict: latest };
                    }
                    return { error: err instanceof Error ? err.message : "Could not apply the change." };
                  }
                },
              }
            : {})}
        />
      ) : null}
    </Page>
  );
}

/**
 * One stacked section of a detail tab (a former tab's view): a small heading over its content, its id the old tab's
 * for `#cost`-style links. A collapsed section mounts its content only once opened, so a terminal or a file list
 * never wakes the object just by being on the page; a link to its id opens it.
 */
export function DetailSection({ id, title, collapsed = false, children }: { id: string; title: string; collapsed?: boolean; children: React.ReactNode }) {
  const targeted = () => typeof window !== "undefined" && window.location.hash === `#${id}`;
  const [open, setOpen] = React.useState(() => !collapsed || targeted());
  const ref = React.useRef<HTMLElement>(null);
  React.useEffect(() => {
    if (targeted()) ref.current?.scrollIntoView({ block: "start" });
    // Only on arrival: the hash names the section the link meant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const heading = <h2 className="text-ui font-medium text-fg-primary">{title}</h2>;
  if (!collapsed) {
    return (
      <section ref={ref} id={id} aria-label={title} className="nd-detail-section mt-8 scroll-mt-4 first:mt-0">
        <div className="mb-3">{heading}</div>
        {children}
      </section>
    );
  }
  return (
    <section ref={ref} id={id} aria-label={title} className="nd-detail-section mt-8 scroll-mt-4 first:mt-0">
      <details open={open} onToggle={(e) => setOpen(e.currentTarget.open)} className="group">
        <summary className="flex w-full cursor-pointer list-none items-center gap-2 select-none [&::-webkit-details-marker]:hidden">
          <ChevronRight className="size-3.5 text-fg-muted transition-transform group-open:rotate-90" aria-hidden />
          {heading}
        </summary>
        {open ? <div className="mt-3">{children}</div> : null}
      </details>
    </section>
  );
}
